import _ from 'lodash';
import { Types } from '../types'; 
import { Product } from '../types'; 
import { Helpers } from '../utils'

export interface ProductState {
  selected: Product.AttributeOptionName[];
  available: Product.AvailableProductAttributes;
}

export interface UpdatedProductStateInput {
  id: Product.ID,
  prevState: ProductState;
  updated?: {
    name: Product.AttributeName;
    value: Product.AttributeOptionName;
  };
  attributes: Product.AvailableProductAttributes;
  variations: Product.VariationAttributes[];
}

const getUpdatedProductState = (props: UpdatedProductStateInput, initial: boolean = false): {state: ProductState, id: Product.ID} => {

	// Destructure updated atrribute meta
	const { name: updatedName, value: updatedValue } = props?.updated ?? {name: '', value: ''};

	const debugID = `getUpdatedProductState(${updatedName}, ${updatedValue})`

	// Helpers.Timer.start(debugID)

	// Destructure variations, attributes 
	const { id: previousVariationID, variations, attributes } = props;

	// Skip/throw error if updatedName is not present in attributeNamess
	//
	if ( _.isEmpty(attributes) ) {
		// console.log( Helpers.Timer.stop(debugID) );
		throw Error(`Required prop "attributes" has not been provided or is empty.`)
	}

	let selectedVariationID = previousVariationID;

	// Initial state
	// NOTE: TODO: Avoid infintite loop if one of the values is undefined on second
	const isInitial = ! Boolean(updatedName && updatedValue) ? true : updatedValue ? initial : false;

	// Get attribute names in theirs default order
	const attributeNames = Object.keys(attributes);

	// Handle initial (selected according to first available attribute)
	if ( ! Boolean(updatedName && updatedValue) ) {
		return getUpdatedProductState({...props, updated: {name: attributeNames[0], value: props.prevState.selected[0]}}, isInitial);
	}
	
	const { selected: prevSelected, available: prevAvailable } = props.prevState;
	
	// Get update attribute name index
	const updatedAttrIndex = attributeNames.indexOf(updatedName);

	// Skip if the prevSelected and updated are the same 
	// NOTE: This causes initial state not to be correctly rendered
	// if ( ! isInitial && prevSelected[updatedAttrIndex] === updatedValue ) {
	// 	// console.log( Helpers.Timer.stop(debugID) );
	// 	return {state: props.prevState, id: selectedVariationID};
	// } 

	// Skip/throw error if updatedName is not present in attributeNamess
	//
	if ( updatedAttrIndex < 0 ) {
		// console.log( Helpers.Timer.stop(debugID) );
		throw Error(`"${updatedName}" is not a valid attribute name.`)
	}
	
	// Skip/throw error if updatedValue is not present in prevAvailable
	if ( ! isInitial && ! (updatedName in prevAvailable ) ) {
		// console.log( Helpers.Timer.stop(debugID) );
		throw Error(`"${updatedName}" is not a valid attribute name.`)
	}
	
	// Skip/throw error if updatedValue is not present in prevAvailable
	if ( ! isInitial && ! prevAvailable[updatedName].includes(updatedValue) ) {
		// console.log( Helpers.Timer.stop(debugID) ); 
		throw Error(`"${updatedValue}" is not contained in "${updatedName}" available values.`)
	}

	// Define new state (merge previous and updated)
	const newState = {...props.prevState, };

	// Set updated selected values
	// - included all previous selected values (including selected)
	// - reset attributes which needs to update selected
	newState.selected = newState.selected.map((value, attrIndex) => 
		Number(attrIndex) === Number(updatedAttrIndex) ? updatedValue : attrIndex < updatedAttrIndex ? value : '' 
	)

	// Return new state with only updates selected values
	// if the last attribute has been updated
	if ( (updatedAttrIndex + 1 ) === attributeNames.length && attributeNames.length > 1 ) {
		// Skip if value is not contained in prevAvailable values of updatedName
		if ( ! prevAvailable[updatedName].includes( updatedValue ) ) {
			// console.log( Helpers.Timer.stop(debugID) );
			throw Error(`Attribute value "${updatedValue}" is not a valid "${updatedName}." `)
		}

		// Try to find matching variation for selected atts (always should exist when last) and return if found
		const selectedVariation = Object.entries(variations).find(([id, variationAtts]) => _.isEqual(newState.selected, variationAtts));
		
		if ( selectedVariation ) {
			return {state: newState, id: Number(selectedVariation[0])};
		}
		
	}

	// Set updated available values
	// - include all previous available values (including selected)
	// - reset attributes which needs to update available
	newState.available = Object.fromEntries(
		Object.entries(newState.available).map(([attrName, attrValues], attrIndex) => 
			[attrName, Number(attrIndex) <= Number(updatedAttrIndex) ? attrValues : []]
		)
	)

	// Iterate over each selected attribute (new copy)
	for ( const selectedAttrIndex in newState.selected ) {
		if ( isInitial && ! (attributeNames[selectedAttrIndex] in newState.available) ) {
			newState.available[attributeNames[selectedAttrIndex]] = [];
		}
	}

	// Only for purposes of setting initial values
	const initiallyAvailableOptions: Product.AvailableProductAttributes = {};

	// Iterate over each variation which meets prev selected values + update
	// TODO: Fuck you, I see you at work. Abstract this callback function for fuck sake, its perfect! :) Yes it is, 420
	Object.entries(variations).forEach(([variationID, variationAtts]) => {

		// Skip variation if was previously active (expect to be different ofc)
		// What if previous will be the fallback one?
		// if ( Number(variationID) === Number(previousVariationID) ) {
		// 	return;
		// }

		// Iterate over each selected attribute (new copy)
		for ( const selectedAttrIndex in newState.selected ) {
			
			// Get attribute name by index
			const selectedAttrName = attributeNames[selectedAttrIndex];

			// Get up to date value (to decrease iters count)
			const selectedAttrValue = newState.selected[selectedAttrIndex];

			// Throw error if selected variable is invalid
			// RETHINK: Or should be replace by first correct one
			// Index problems i guess
			if ( selectedAttrValue && ! attributes[ selectedAttrName ].includes( selectedAttrValue ) ) {
	
				throw Error(`Attribute value "${updatedValue}" is not a valid "${updatedName}." ${JSON.stringify(attributes[ selectedAttrName ])}`)
			}

			// HOTFIX: Include all first attribute values without additionl check if is initial
			// Store it separetely until other are evaluated
			if ( initial && Number(selectedAttrIndex) === Number(updatedAttrIndex) ) {
				if ( ! (selectedAttrName in initiallyAvailableOptions) ) {
					initiallyAvailableOptions[selectedAttrName] = [];
				}
				if ( ! initiallyAvailableOptions[selectedAttrName].includes(variationAtts[selectedAttrIndex]) ) {
					initiallyAvailableOptions[selectedAttrName].push(variationAtts[selectedAttrIndex]);
				}
			}
			
			// Skip variation if one of attribute value is not same as updated
			if ( selectedAttrValue ) {
				// Skip if variation's attribute value is diff from prev selected
				if ( selectedAttrValue !== variationAtts[selectedAttrIndex] && updatedAttrIndex >= Number(selectedAttrIndex) ) {
					console.log('skipping', selectedAttrValue);
					return;
				}
				selectedVariationID = Number(variationID);
			} else {
					// Select first available variation attribute value
					if ( prevSelected[selectedAttrIndex] === variationAtts[selectedAttrIndex] ) {
					newState.selected[selectedAttrIndex] = prevSelected[selectedAttrIndex];
					selectedVariationID = Number(variationID);
				}
			} 

			// Handle last nonselected
			// option to reset or first next exsting
			// ''
			if ( ! newState.selected[selectedAttrIndex] ) {
				// Probably need to run this block again recurisvely to get valid variation
				newState.selected[selectedAttrIndex] = variationAtts[selectedAttrIndex];
				const selectedVariation = Object.entries(variations).find(([id, variationAtts]) => _.isEqual(newState.selected, variationAtts))
		
				// If nearest found, update variation ID
				if ( selectedVariation ) {
					selectedVariationID = Number(selectedVariation[0]);
				}
			}

			// // console.log(
			// 	selectedAttrName, variationAtts,
			// 	'\n------------------------------\n',
			// 	'selectedAttrValue: ', selectedAttrValue, '\n',
			// 	'prevSelectedValue: ', prevSelected[selectedAttrIndex], '\n',
			// 	'variationAttrValue: ', variationAtts[selectedAttrIndex],
			// 	'\n------------------------------\n'
			// )

			// Add to available if not present
			if ( ! newState.available[selectedAttrName].includes(variationAtts[selectedAttrIndex]) ) {
				newState.available[selectedAttrName].push(variationAtts[selectedAttrIndex]);
			}
				
		}

	}) 

	// Include initially available if any
	newState.available = {...newState.available, ...initiallyAvailableOptions}

	// console.log( Helpers.Timer.stop(debugID) );

	return {state: newState, id: selectedVariationID};
}

export default getUpdatedProductState;
